import { useState, useEffect } from 'react';

interface GoldPriceData {
  price: number;
  pricePerOunce: number;
  lastUpdated: string;
  isLoading: boolean;
  error: string | null;
  source?: string;
  isLive?: boolean;
}

/**
 * Live gold price via server proxy GET /api/gold-price
 * (avoids browser CoinGecko CORS/rate-limit → EMERGENCY FALLBACK).
 */
export const useGoldPrice = () => {
  const [goldPrice, setGoldPrice] = useState<GoldPriceData>({
    price: 169000,
    pricePerOunce: 5257,
    lastUpdated: '',
    isLoading: true,
    error: null,
    source: 'Loading...',
    isLive: false,
  });

  const fetchGoldPrice = async () => {
    setGoldPrice((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${base}/api/gold-price`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.success || !Number.isFinite(Number(data.price))) {
        throw new Error(data?.error || 'Invalid gold price response');
      }

      setGoldPrice({
        price: Number(data.price),
        pricePerOunce: Number(data.pricePerOunce) || Math.round((Number(data.price) / 32.15) * 100) / 100,
        lastUpdated: data.lastUpdated
          ? new Date(data.lastUpdated).toLocaleString()
          : new Date().toLocaleString(),
        isLoading: false,
        error: data.error || null,
        source: data.source || 'Unknown',
        isLive: Boolean(data.isLive),
      });
    } catch (error) {
      console.error('Critical error in fetchGoldPrice:', error);
      setGoldPrice((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Failed to fetch price, using fallback',
        source: 'Error Fallback',
        isLive: false,
      }));
    }
  };

  useEffect(() => {
    fetchGoldPrice();
    const interval = setInterval(fetchGoldPrice, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    ...goldPrice,
    refetch: fetchGoldPrice,
  };
};
